import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextLink from 'LuxKit/TextLink'
import type { CalendarPickerMonthView } from 'selectors/offerPage/offerPageSelectors'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'

interface Props {
  duration: number
  flightsOnly?: boolean
  enableCalendarRecommendations: boolean
  month: CalendarPickerMonthView
  selected?: boolean
  hasPromo: boolean
  displayMemberPrice?: boolean
  isMultiRoom?: boolean
}

enum DisplayVariant {
  ShowContentPackageAvailable,
  ShowFlightsNotYetAvailable,
  ShowRecommendations,
  ShowFlightOnlyNoCache,
}

function OfferCalendarFlightsMonthTile(props: Props) {
  const {
    month,
    duration,
    flightsOnly,
    enableCalendarRecommendations,
    selected,
    hasPromo,
    displayMemberPrice,
    isMultiRoom,
  } = props

  const intl = useIntl()
  const flightPriceAvailable = month.hotelTotal < month.packagedTotal

  const displayVariant = useMemo<DisplayVariant>(() => {
    if (month.hotelAvailable) {
      if (month.packagedAvailable) {
        if (flightsOnly && !month.flightCacheActive) {
          return DisplayVariant.ShowFlightOnlyNoCache
        }
        return DisplayVariant.ShowContentPackageAvailable
      } else if (!flightsOnly) {
        return DisplayVariant.ShowFlightsNotYetAvailable
      }
    }
    return DisplayVariant.ShowRecommendations
  }, [month.hotelAvailable, month.packagedAvailable, month.flightCacheActive, flightsOnly])

  const colour = useMemo(() => {
    if (hasPromo) {
      return 'highlight-secondary'
    } else if (displayMemberPrice && !selected) {
      return 'lux-plus'
    } else {
      return undefined
    }
  }, [hasPromo, displayMemberPrice, selected])

  const subTextColour = selected ? 'neutral-one' : 'neutral-three'

  const nightsWithFlightsFromLabel = intl.formatMessage({
    id: 'MWMCLYa+Vd',
    defaultMessage: '{nights, plural, one {# night} other {# nights}} + flights from',
    description: 'Night count with flights and from label for flights calendar month tile',
  }, { nights: duration })
  const nightsFromLabel = intl.formatMessage({
    id: 'yt5z51796m',
    defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
    description: 'Night count with from label for flights calendar month tile',
  }, { nights: duration })

  return (<div>
    {displayVariant === DisplayVariant.ShowFlightOnlyNoCache && (
      <Caption variant="medium" colour={subTextColour}>
        {intl.formatMessage({
          id: 'LjCzrv8oB4',
          defaultMessage: '{nights, plural, one {# night} other {# nights}}',
          description: 'Night count for flights calendar month tile when no flight cache',
        }, { nights: duration })}
      </Caption>
    )}
    {displayVariant === DisplayVariant.ShowRecommendations && enableCalendarRecommendations && (
      <>
        <Caption variant="medium" colour={subTextColour}>
          {intl.formatMessage({
            id: 'O55VmJNOxT',
            defaultMessage: 'Sold out',
            description: 'Text shown when flights month tile is sold out',
          })}
        </Caption>
        <TextLink size="small" weight="regular">
          {intl.formatMessage({
            id: 'd9YMsCYm32',
            defaultMessage: 'View similar offers',
            description: 'Link text to view similar offers when flights month tile is sold out',
          })}
        </TextLink>
      </>
    )}
    {
      displayVariant === DisplayVariant.ShowContentPackageAvailable && (<>
        {!isMultiRoom && <Caption variant="medium" colour={subTextColour}>
          {flightPriceAvailable ? nightsWithFlightsFromLabel : nightsFromLabel}
        </Caption>}
        {isMultiRoom && <Caption variant="medium" colour={subTextColour}>
          <MobileMultipleRoomsDurationLabel shouldDisplayRoom shouldDisplayFlight />
        </Caption>}
        <div>
          <BodyText colour={colour} as="span" variant="large" weight="bold">
            <FormatCurrency value={month.packagedTotal} />
          </BodyText>{' '}
          <Caption as="span" variant="medium" colour={colour ?? subTextColour}>
            {intl.formatMessage({
              id: 'nQzR0tKhE2',
              defaultMessage: 'total*',
              description: 'Total price label with asterisk for flights calendar month tile',
            })}
          </Caption>
          {!flightPriceAvailable &&
            <Caption as="span" variant="medium" colour={subTextColour}>
              {' '}{intl.formatMessage({
                id: 'eULbk0jd8h',
                defaultMessage: '+ flights',
                description: 'Additional flights label for flights calendar month tile',
              })}
            </Caption>}
        </div>
      </>)
    }
    {
      displayVariant === DisplayVariant.ShowFlightsNotYetAvailable && (<>
        <Caption variant="medium" colour={subTextColour}>
          {intl.formatMessage({
            id: 'NM40McNj+Q',
            defaultMessage: 'Flights not yet available',
            description: 'Message shown when flights are not yet available for a month in flights month tile',
          })}
        </Caption>
        <TextLink size="small" weight="regular">
          {intl.formatMessage({
            id: 'NybcRWR1jn',
            defaultMessage: 'View hotel only',
            description: 'Link text to view hotel-only pricing when flights are not yet available',
          })}
        </TextLink>
      </>)
    }
  </div>
  )
}

export default React.memo(OfferCalendarFlightsMonthTile)
